/* StevePartUtils.js */

'use strict';
const _ = require('lodash');

function dynamicObjectCreator(constructorFn, args) {
  function F() {
    return constructorFn.apply(this, args);
  }

  F.prototype = constructorFn.prototype;
  return new F();
}

function retreiveInjector(domElem) {
  let injector;
  if (_.isFunction(domElem.injector)) {
    injector = domElem.injector();
  }
  if (!injector) {
    const parents = domElem.parents();
    _.every(parents, function (parent) {
      //Crawl the dom until you find an injector and use that
      if (_.isFunction(parent.injector)) {
        injector = parent.injector();
      }
      return !injector;
    });
  }
  return injector;
}

module.exports = {
  /**
   * Looks at a Steve Part constructor and handles the startup and retreival of the Parts API.
   *
   * If the Steve Part is an Angular part it will handle the $init and $compiledAndLinked phases.
   * If it is not it will use the new operator and create the part and return the API object.
   *
   * @param {object} [options] Optional details of part launching.
   * @param {DomElem} [options.containerElement] If specified will be used to locate the angular Injector if one
   * is not found via the jqElem. This is really handy if the jqElem is not attached to the DOM yet.
   *
   * @returns {promise} When resolved provides the the API object associated with this object
   */
  launchPart: async function (constructor, jqElem, args, options) {
    const api = dynamicObjectCreator(constructor, args);
    let $scope;
    let $injector;
    if (constructor.isAngularPart) {
      $injector = retreiveInjector(jqElem);
      if (!$injector && options && options.containerElement) {
        $injector = retreiveInjector(options.containerElement);
        if (!$injector) {
          throw new Error('Cannot find a $injector for angular Part.');
        } else {
          $scope = options.containerElement.scope();
        }
      } else {
        $scope = jqElem.scope();
      }

      //create $scope that prototypically inherits from its parent dom element
      const newScope = $scope.$new(false);
      let initProm;

      //Init the Part
      if (_.isFunction(api.$init)) {
        //If the Part has specified injectables
        if (_.isArray(api.$init.$inject)) {
          initProm = $injector.invoke(api.$init, api, {
            $scope: newScope,
          });
        } else {
          initProm = api.$init(newScope);
        }
      }
      //TODO PromiseCheck to be changed
      if (!initProm || initProm.toString() !== '[object Promise]') {
        initProm = Promise.resolve();
      }

      await initProm;

      let compileProm;
      const $compile = $injector.get('$compile');
      $compile(jqElem)(newScope);

      //Notify the Part that compile/link phase is complete
      if (_.isFunction(api.$compiledAndLinked)) {
        const $timeout = $injector.get('$timeout');
        //***NOTE*** Angular provides no way in knowing when a component is finished being compiled/linked this is the hacky way to do it...
        $timeout(function () {
          //grab the providers
          if (_.isArray(api.$compiledAndLinked.$inject)) {
            compileProm = $injector.invoke(api.$compiledAndLinked, api, {$scope: newScope});
          } else {
            compileProm = api.$compiledAndLinked(newScope);
          }
        });
      }

      //TODO PromiseCheck to be changed
      if (!compileProm || compileProm.toString() !== '[object Promise]') {
        compileProm = Promise.resolve();
      }
      await compileProm;
      //The api object is actually the $scope property of the domElem
      return newScope;
    } else {
      return api;
    }
  },
};
